import axios from 'axios';
import { PuzzleSettings, type PuzzleSettingsDto } from 'endchess-contracts';
import getApiInstance from '../common/apiInstance';
import { getApiErrorMessage } from '../common/apiErrorMessage';
import { isEntitlementError } from '../subscription/entitlementError';
import {
  DEFAULT_PLAYER_RATING,
  defaultRangeForRating,
  easyRangeForRating,
  hardRangeForRating,
  type PuzzleRatingBounds,
} from '../puzzles/puzzleRatingRange';
import type { PuzzleStatsResponse } from './types';

export type PuzzlePeekResponse = {
  _id: string;
  FEN: string;
  Moves: string;
  Rating?: number;
};

export type DashboardPuzzlePeek = {
  id: string;
  fen: string;
  rating?: number;
};

const puzzlesApi = getApiInstance('/puzzles', 'no-settings');

// Puzzle stats must bypass the browser cache (304), or rating bounds can stay stale
// after catalog imports until a full page refresh.
const PUZZLE_STATS_GET_CONFIG = {
  headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
} as const;

const puzzleStatsParams = (): Record<string, number> => ({
  _: Date.now(),
});

const puzzlePeekGetConfig = {
  ...PUZZLE_STATS_GET_CONFIG,
  params: puzzleStatsParams(),
  skipGlobalErrorToast: true,
  skipAuthRedirect: true,
} as const;

/** Dashboard previews may include already-solved puzzles in the band. */
const buildPuzzlePeekSettings = (
  ratingMin: number,
  ratingMax: number,
): string => {
  const settings: PuzzleSettingsDto = {
    solvedStatuses: [
      PuzzleSettings.SolvedStatus.SOLVED,
      PuzzleSettings.SolvedStatus.UNSOLVED,
    ],
    ratingMin,
    ratingMax,
  };
  return JSON.stringify(settings);
};

export const normalizeDashboardPlayerRating = (puzzleElo: number): number => {
  const rounded = Math.round(puzzleElo);
  return Number.isFinite(rounded) && rounded > 0
    ? rounded
    : DEFAULT_PLAYER_RATING;
};

const isPeekNotFound = (error: unknown): boolean =>
  axios.isAxiosError(error) && error.response?.status === 404;

const requestPuzzlePeek = async (
  ratingMin: number,
  ratingMax: number,
): Promise<PuzzlePeekResponse> => {
  const settings = buildPuzzlePeekSettings(ratingMin, ratingMax);
  const peekApi = getApiInstance('/puzzles', settings);
  const { data } = await peekApi.get<PuzzlePeekResponse>(
    '/peek',
    puzzlePeekGetConfig,
  );
  return data;
};

export const fetchPuzzlePeek = async (
  ratingMin: number,
  ratingMax: number,
): Promise<PuzzlePeekResponse> => {
  try {
    return await requestPuzzlePeek(ratingMin, ratingMax);
  } catch (error) {
    throw new Error(
      getApiErrorMessage(error, { action: 'load puzzle preview' }),
    );
  }
};

export const fetchPuzzleStats = async (): Promise<PuzzleStatsResponse> => {
  const { data } = await puzzlesApi.get<PuzzleStatsResponse>('/stats', {
    ...PUZZLE_STATS_GET_CONFIG,
    params: puzzleStatsParams(),
    skipGlobalErrorToast: true,
    skipAuthRedirect: true,
  });
  return data;
};

export const fetchPuzzleRating = async (): Promise<number | null> => {
  try {
    const stats = await fetchPuzzleStats();
    return Math.round(stats.puzzle_elo);
  } catch {
    return null;
  }
};

const toDashboardPuzzle = (peek: PuzzlePeekResponse): DashboardPuzzlePeek => ({
  id: peek._id,
  fen: peek.FEN,
  rating: peek.Rating,
});

export const fetchEasyAndHardPuzzles = async (
  playerRating: number,
  ratingBounds: PuzzleRatingBounds,
): Promise<{
  easy: DashboardPuzzlePeek | null;
  hard: DashboardPuzzlePeek | null;
  entitlementError: unknown;
}> => {
  const [easyMin, easyMax] = easyRangeForRating(playerRating, ratingBounds);
  const [hardMin, hardMax] = hardRangeForRating(playerRating, ratingBounds);

  const [easyResult, hardResult] = await Promise.all([
    requestPuzzlePeek(easyMin, easyMax).then(
      (value) => ({ status: 'fulfilled' as const, value }),
      (reason) => ({ status: 'rejected' as const, reason }),
    ),
    requestPuzzlePeek(hardMin, hardMax).then(
      (value) => ({ status: 'fulfilled' as const, value }),
      (reason) => ({ status: 'rejected' as const, reason }),
    ),
  ]);

  const entitlementError =
    (easyResult.status === 'rejected' && isEntitlementError(easyResult.reason)
      ? easyResult.reason
      : null) ??
    (hardResult.status === 'rejected' && isEntitlementError(hardResult.reason)
      ? hardResult.reason
      : null);

  let easy =
    easyResult.status === 'fulfilled'
      ? toDashboardPuzzle(easyResult.value)
      : null;
  const hard =
    hardResult.status === 'fulfilled'
      ? toDashboardPuzzle(hardResult.value)
      : null;

  if (!easy && !hard && !entitlementError) {
    const rejected = [easyResult, hardResult].filter(
      (result): result is { status: 'rejected'; reason: unknown } =>
        result.status === 'rejected',
    );
    const blockingError = rejected.find(
      (result) =>
        !isPeekNotFound(result.reason) &&
        !isEntitlementError(result.reason),
    );
    if (blockingError) {
      throw blockingError.reason;
    }

    const [fallbackMin, fallbackMax] = defaultRangeForRating(
      playerRating,
      ratingBounds,
    );
    try {
      const fallback = await requestPuzzlePeek(fallbackMin, fallbackMax);
      easy = toDashboardPuzzle(fallback);
    } catch {
      // Empty catalog — leave both cards empty.
    }
  }

  return {
    easy,
    hard,
    entitlementError,
  };
};
